import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

/**
 * Web counterpart of DeepLinkListener.jsx's native OAuth handling. Reached
 * at APP_OAUTH2_REDIRECT_URI_WEB (backend's auth.controller.ts
 * redirectWithTokens, see env.oauthRedirectWeb) as a plain https:// page —
 * there's no `appUrlOpen` event outside Capacitor, so the desktop/browser
 * flow lands here instead and reads the tokens straight from the query
 * string.
 */
export default function OAuthCallbackScreen() {
    const navigate = useNavigate();
    const { loginWithTokens } = useAuth();
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;

        // The app uses HashRouter, so the backend redirect target
        // (APP_OAUTH2_REDIRECT_URI_WEB, e.g. https://host/#/oauth2/callback)
        // puts the tokens in the query *inside* the hash. Fall back to the
        // real query string for a redirect target without a hash.
        const hashQuery = window.location.hash.split('?')[1] ?? '';
        const params = new URLSearchParams(hashQuery || window.location.search);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        if (!accessToken || !refreshToken) {
            navigate('/online/login', { replace: true });
            return;
        }

        loginWithTokens(accessToken, refreshToken)
            .then(() => navigate('/online', { replace: true }))
            .catch(() => navigate('/online/login', { replace: true }));
    }, [navigate, loginWithTokens]);

    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
        </Box>
    );
}
